import type { ReactNode } from "react";

import type {
  BreakerDeviceResponse,
  BreakerSelectivityResponse,
} from "../services/breakerSelectivity";
import type { DeviceRole } from "../services/breakerSelectivityContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { STATUS_LABELS, statusHeading } from "./BreakerSelectivityResultSummary";
import { SOURCE_LABELS } from "./StarterCoordinationResultSummary";
import {
  BREAKER_KIND_LABELS,
  CASE_LABELS,
  DUTY_BASIS_LABELS,
  FAULT_TYPE_LABELS,
} from "./breakerSelectivityDraft";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";

interface BreakerSelectivityResultPanelProps {
  result: BreakerSelectivityResponse;
}

/** Carried by every result (A21 (g)): what the checks are and are not. */
export const EVIDENCE_NOTE =
  "Breaker duty, selectivity and cascading evidence is manufacturer-declared; this check does " +
  "not prove them (GAP-021).";

// The six words of the Result column. A check is true (passed), false (failed, a
// warning names it - or a review: declared cascading, a rated voltage below the system
// voltage (IR-001 F1) - or partial: a declared limit below Ik'' where selectivity is not
// required, which is neither a pass nor a failure) or null: not made, because no peak
// was held for the making check, or because no selectivity evidence is declared
// (A21 (b)-(d)).
export const RESULT_WORDS = {
  pass: "Pass",
  fail: "Fails",
  review: "Review",
  partial: "Partial",
  notEvaluated: "Not evaluated",
  notEstablished: "Not established",
} as const;

/** The words of a device's place on the bus, typed against the contract enum. */
export const ROLE_LABELS: Record<DeviceRole, string> = {
  INCOMER: "Incomer",
  OUTGOING: "Outgoing",
};

type NullMeaning = "notEvaluated" | "notEstablished";
type FalseMeaning = "fail" | "review" | "partial";

function resultWord(check: boolean | null, whenNull: NullMeaning, whenFalse: FalseMeaning): string {
  if (check === null) {
    return RESULT_WORDS[whenNull];
  }
  return check ? RESULT_WORDS.pass : RESULT_WORDS[whenFalse];
}

function resultKey(check: boolean | null, whenNull: NullMeaning, whenFalse: FalseMeaning): string {
  if (check === null) {
    return whenNull === "notEvaluated" ? "not-evaluated" : "not-established";
  }
  return check ? "pass" : whenFalse;
}

// One cell holding an engineering figure: four significant figures with the unit on
// screen, the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null; unit: string }) {
  const quantity = formatQuantity(value, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

// A rating with the margin the engine reported beside it: "Icu 50 kA · margin 14.78 kA".
function RatingWithMargin({
  name,
  rating,
  unit,
  margin,
}: {
  name: string;
  rating: string;
  unit: string;
  margin: string | null;
}) {
  const value = formatQuantity(rating, unit);
  const spare = formatQuantity(margin, "kA");
  return (
    <td title={value.exact ?? undefined}>
      {name} {value.display} · margin {spare.display}
    </td>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

// The fault level every device was held against, as the stored fault run holds it -
// or as typed, with no run, case or fault type (A21 (a)). ip is empty when it was not held.
function FaultLevelTable({ result }: { result: BreakerSelectivityResponse }) {
  const linked = result.current_source === "LINKED_FAULT_RUN";
  const current = formatQuantity(result.prospective_current_ka, "kA");
  const peak = formatQuantity(result.peak_current_ka, "kA");
  const voltage = formatQuantity(result.system_nominal_voltage_v, "V");

  return (
    <ValueTable title="Fault level">
      <tr data-row-key="current_source">
        <th scope="row">Fault level source</th>
        <td data-current-source={result.current_source}>{SOURCE_LABELS[result.current_source]}</td>
      </tr>
      <tr data-row-key="fault_run_id">
        <th scope="row">Fault run</th>
        {/* The stored run's id: the evidence the fault level was read from. */}
        <td>{result.fault_run_id ?? EMPTY_QUANTITY}</td>
      </tr>
      <tr data-row-key="fault_bus_code">
        <th scope="row">Fault bus</th>
        <td>{result.fault_bus_code}</td>
      </tr>
      <tr data-row-key="bus_code">
        <th scope="row">Study bus</th>
        <td>{result.bus_code}</td>
      </tr>
      <tr data-row-key="calculation_case">
        <th scope="row">Calculation case</th>
        <td>
          {linked && result.calculation_case
            ? CASE_LABELS[result.calculation_case]
            : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="fault_type">
        <th scope="row">Fault type</th>
        <td>
          {linked && result.fault_type ? FAULT_TYPE_LABELS[result.fault_type] : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="prospective_current_ka">
        <th scope="row">Prospective current Ik'' (kA)</th>
        <td title={current.exact ?? undefined}>{current.display}</td>
      </tr>
      <tr data-row-key="peak_current_ka">
        <th scope="row">Peak current ip (kA)</th>
        <td title={peak.exact ?? undefined}>{peak.display}</td>
      </tr>
      <tr data-row-key="system_nominal_voltage_v">
        <th scope="row">System nominal voltage (V)</th>
        <td title={voltage.exact ?? undefined}>{voltage.display}</td>
      </tr>
    </ValueTable>
  );
}

type Check = {
  key: string;
  label: string;
  value: ReactNode;
  limit: ReactNode;
  check: boolean | null;
  whenNull: NullMeaning;
  whenFalse: FalseMeaning;
};

// The selectivity declaration towards the incomer as the engine held it: total, partial
// up to a limit with its margin, or none (A21 (d)).
function selectivityLimit(device: BreakerDeviceResponse): ReactNode {
  if (device.declared_selectivity_kind === "TOTAL") {
    return <td>Total</td>;
  }
  if (device.declared_selectivity_kind === "PARTIAL" && device.selectivity_limit_ka !== null) {
    const limit = formatQuantity(device.selectivity_limit_ka, "kA");
    const margin = formatQuantity(device.selectivity_margin_ka, "kA");
    const required = device.selectivity_required === false ? " · not required" : "";
    return (
      <td title={limit.exact ?? undefined}>
        Partial {limit.display} · margin {margin.display}
        {required}
      </td>
    );
  }
  return <td>{EMPTY_QUANTITY}</td>;
}

// One device: each duty check beside the figure it was held against, the selectivity
// and cascading declarations of an outgoing device, then what else it declared. Every
// figure is the engine's; nothing is compared or recomputed here.
function DeviceTable({
  device,
  incomerCode,
  prospectiveCurrentKa,
  peakCurrentKa,
  systemVoltageV,
}: {
  device: BreakerDeviceResponse;
  incomerCode: string;
  prospectiveCurrentKa: string;
  peakCurrentKa: string | null;
  systemVoltageV: string;
}) {
  const title = `${ROLE_LABELS[device.role]} ${device.code} — ${STATUS_LABELS[device.status]}`;
  const outgoing = device.role === "OUTGOING";
  const cascading = device.relies_on_cascading === true;
  const ratedCurrent = formatQuantity(device.rated_current_a, "A");
  const cascadingLimit = formatQuantity(device.cascading_limit_ka, "kA");

  const checks: Check[] = [
    {
      key: "icu",
      label: "Breaking capacity Icu",
      value: <Quantity value={prospectiveCurrentKa} unit="kA" />,
      limit: (
        <RatingWithMargin
          name="Icu"
          rating={device.ultimate_breaking_capacity_ka}
          unit="kA"
          margin={device.icu_margin_ka}
        />
      ),
      check: device.icu_adequate,
      whenNull: "notEvaluated",
      // A declared cascading limit that covers Ik'' turns the failure into a review (A21 (c)).
      whenFalse: cascading ? "review" : "fail",
    },
  ];
  if (cascading) {
    checks.push({
      key: "cascading",
      label: "Relies on cascading",
      value: <Quantity value={prospectiveCurrentKa} unit="kA" />,
      limit: (
        <td title={cascadingLimit.exact ?? undefined}>Cascading limit {cascadingLimit.display}</td>
      ),
      // Never a pass: the engineer must confirm the manufacturer's cascading table.
      check: false,
      whenNull: "notEvaluated",
      whenFalse: "review",
    });
  }
  if (device.duty_basis === "SERVICE" && device.service_breaking_capacity_ka !== null) {
    checks.push({
      key: "ics",
      label: "Service breaking capacity Ics",
      value: <Quantity value={prospectiveCurrentKa} unit="kA" />,
      limit: (
        <RatingWithMargin
          name="Ics"
          rating={device.service_breaking_capacity_ka}
          unit="kA"
          margin={device.ics_margin_ka}
        />
      ),
      check: device.ics_adequate,
      whenNull: "notEvaluated",
      whenFalse: "fail",
    });
  }
  checks.push(
    {
      key: "icm",
      label: "Making capacity Icm",
      value: <Quantity value={peakCurrentKa} unit="kA" />,
      limit: (
        <RatingWithMargin
          name="Icm"
          rating={device.making_capacity_ka}
          unit="kA peak"
          margin={device.icm_margin_ka}
        />
      ),
      check: device.icm_adequate,
      // No peak held: the check was not made (A21 (b)).
      whenNull: "notEvaluated",
      whenFalse: "fail",
    },
    {
      key: "voltage",
      label: "Rated operational voltage Ue",
      value: <Quantity value={systemVoltageV} unit="V" />,
      limit: <Quantity value={device.rated_operational_voltage_v} unit="V" />,
      check: device.voltage_adequate,
      whenNull: "notEvaluated",
      // Below the system voltage is a review, not a failure (IR-001 F1).
      whenFalse: "review",
    },
  );
  if (outgoing) {
    checks.push({
      key: "selectivity",
      label: `Selectivity with ${incomerCode}`,
      value: <Quantity value={prospectiveCurrentKa} unit="kA" />,
      limit: selectivityLimit(device),
      check: device.selective,
      // No declaration: the evidence is not established (A21 (d)).
      whenNull: "notEstablished",
      // A partial limit below Ik'' fails a required selectivity; where selectivity is not
      // required the pair is simply not selective at Ik'' - neither a pass nor a failure.
      whenFalse: device.selectivity_required === false ? "partial" : "fail",
    });
  }

  return (
    <section
      aria-label={title}
      data-device={device.code}
      data-device-role={device.role}
      data-device-status={device.status}
    >
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Check</th>
              <th scope="col">Value</th>
              <th scope="col">Limit or requirement</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((item) => (
              <tr key={item.key} data-check-key={item.key}>
                <th scope="row">{item.label}</th>
                {item.value}
                {item.limit}
                <td data-check-result={resultKey(item.check, item.whenNull, item.whenFalse)}>
                  {/* The word in a pill (A25 (d)): the cell keeps the outcome attribute and the
                      text; the span is the styling hook of the pill alone. */}
                  <span data-check-pill>
                    <StatusIcon tone={checkTone(resultKey(item.check, item.whenNull, item.whenFalse))} size={16} />
                    {resultWord(item.check, item.whenNull, item.whenFalse)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* What else the device declared; none of it changes a check. */}
      <p data-device-echo={device.code}>
        <span data-breaker-kind={device.kind}>{BREAKER_KIND_LABELS[device.kind]}</span>
        {" · In "}
        <span title={ratedCurrent.exact ?? undefined}>{ratedCurrent.display}</span>
        {" · "}
        <span data-duty-basis={device.duty_basis}>{DUTY_BASIS_LABELS[device.duty_basis]}</span>
      </p>
    </section>
  );
}

export function BreakerSelectivityResultPanel({ result }: BreakerSelectivityResultPanelProps) {
  const devices = [result.incomer, ...result.outgoing];

  return (
    <article aria-label="Breaker selectivity result">
      <header>
        <h2>Breaker selectivity result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status:{" "}
          <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <FaultLevelTable result={result} />

      {devices.map((device) => (
        <DeviceTable
          key={device.code}
          device={device}
          incomerCode={result.incomer.code}
          prospectiveCurrentKa={result.prospective_current_ka}
          peakCurrentKa={result.peak_current_ka}
          systemVoltageV={result.system_nominal_voltage_v}
        />
      ))}

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The checks themselves carry
          no profile-dependent value; the profile is recorded with the run and must match the
          design basis of the project revision.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the linked fault study, every
          breaker rating, every selectivity and cascading table and its conditions before
          approval or issue. {EVIDENCE_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default BreakerSelectivityResultPanel;
